import { useEffect, useRef, useState } from "react";
// import "../../components/editorForm/forms/FormFields.css";
import { ChevronDown } from "lucide-react";

export default function QRDesignerComponent({ role }) {
    const canvasRef = useRef(null);

    const [text, setText] = useState("https://jambojar.com/");

    // Dynamic size
    const [qrWidth, setQrWidth] = useState(2000);
    const [qrHeight, setQrHeight] = useState(2000);

    const [color1, setColor1] = useState("#000000");
    const [transparentBg, setTransparentBg] = useState(false);
    const [bgColor, setBgColor] = useState("#ffffff");

    const [logo, setLogo] = useState(null);

    // Pattern + corner styles
    const [patternStyle, setPatternStyle] = useState("round");
    const [cornerStyle, setCornerStyle] = useState("square");

    // Toggle scanner corners
    const [showCorners, setShowCorners] = useState(false);
    const [margin, setMargin] = useState(8);

    function isLightColor(hex) {
        const c = hex.substring(1);
        const rgb = parseInt(c, 16);

        const r = (rgb >> 16) & 255;
        const g = (rgb >> 8) & 255;
        const b = rgb & 255;

        const brightness = (r * 299 + g * 587 + b * 114) / 1000;

        return brightness > 200;
    }

    useEffect(() => {
        generateQR();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        text,
        qrWidth,
        qrHeight,
        color1,
        logo,
        transparentBg,
        bgColor,
        patternStyle,
        cornerStyle,
        showCorners,
        margin
    ]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    function generateQR() {
        if (!window.QRCode) return;

        const qr = new window.QRCode(document.createElement("div"), {
            text,
            width: 300,
            height: 300,
            correctLevel: window.QRCode.CorrectLevel.H,
        });

        const matrix = qr._oQRCode.modules;

        drawQR(matrix);
    }

    // =========================================================
    // PATTERN DRAWER
    // =========================================================

    function drawPattern(ctx, style, x, y, cell) {
        const cx = x + cell / 2;
        const cy = y + cell / 2;

        if (style === "square") {
            ctx.fillRect(x, y, cell, cell);
        }

        else if (style === "round") {
            ctx.beginPath();
            ctx.arc(cx, cy, cell * 0.42, 0, Math.PI * 2);
            ctx.fill();
        }

        else if (style === "diamond") {
            ctx.save();

            ctx.translate(cx, cy);
            ctx.rotate(Math.PI / 4);

            ctx.fillRect(
                -cell * 0.35,
                -cell * 0.35,
                cell * 0.7,
                cell * 0.7
            );

            ctx.restore();
        }

        else if (style === "dot") {
            ctx.beginPath();
            ctx.arc(cx, cy, cell * 0.3, 0, Math.PI * 2);
            ctx.fill();
        }

        else if (style === "star") {
            const spikes = 5;
            const step = Math.PI / spikes;

            ctx.beginPath();

            for (let i = 0; i < spikes * 2; i++) {
                const radius =
                    i % 2 === 0
                        ? cell * 0.45
                        : cell * 0.2;

                const sx =
                    cx + Math.cos(i * step) * radius;

                const sy =
                    cy + Math.sin(i * step) * radius;

                if (i === 0) {
                    ctx.moveTo(sx, sy);
                } else {
                    ctx.lineTo(sx, sy);
                }
            }

            ctx.closePath();
            ctx.fill();
        }
    }

    // =========================================================
    // CORNER DRAWER
    // =========================================================

    function drawCorner(ctx, style, x, y, size) {

        if (style === "square") {
            ctx.strokeRect(x, y, size, size);

            ctx.fillRect(
                x + size * 0.3,
                y + size * 0.3,
                size * 0.4,
                size * 0.4
            );
        }

        else if (style === "round") {
            ctx.beginPath();

            ctx.roundRect(
                x,
                y,
                size,
                size,
                size * 0.25
            );

            ctx.stroke();

            ctx.beginPath();

            ctx.arc(
                x + size / 2,
                y + size / 2,
                size * 0.2,
                0,
                Math.PI * 2
            );

            ctx.fill();
        }

        else if (style === "circle") {
            ctx.beginPath();

            ctx.arc(
                x + size / 2,
                y + size / 2,
                size / 2,
                0,
                Math.PI * 2
            );

            ctx.stroke();

            ctx.beginPath();

            ctx.arc(
                x + size / 2,
                y + size / 2,
                size * 0.2,
                0,
                Math.PI * 2
            );

            ctx.fill();
        }
    }

    // =========================================================
    // SCANNER BORDER
    // =========================================================

    function drawScannerBorder(ctx, canvasSize, padding, color) {

        const borderLength = canvasSize * 0.14;
        const offset = padding * 0.98;
        const thickness = canvasSize * 0.045;

        ctx.strokeStyle = color;
        ctx.lineWidth = thickness;
        ctx.lineCap = "square";

        const left = padding - offset;
        const right = canvasSize - padding + offset;
        const top = padding - offset;
        const bottom = canvasSize - padding + offset;

        ctx.beginPath();

        // TOP LEFT
        ctx.moveTo(left, top + borderLength);
        ctx.lineTo(left, top);
        ctx.lineTo(left + borderLength, top);

        // TOP RIGHT
        ctx.moveTo(right - borderLength, top);
        ctx.lineTo(right, top);
        ctx.lineTo(right, top + borderLength);

        // BOTTOM RIGHT
        ctx.moveTo(right, bottom - borderLength);
        ctx.lineTo(right, bottom);
        ctx.lineTo(right - borderLength, bottom);

        // BOTTOM LEFT
        ctx.moveTo(left + borderLength, bottom);
        ctx.lineTo(left, bottom);
        ctx.lineTo(left, bottom - borderLength);

        ctx.stroke();
    }

    // =========================================================
    // DRAW QR
    // =========================================================

    function drawQR(matrix) {

        const canvas = canvasRef.current;

        const ctx = canvas.getContext("2d");

        const count = matrix.length;

        canvas.width = qrWidth;
        canvas.height = qrHeight;

        const canvasSize = Math.min(qrWidth, qrHeight);

        const padding = canvasSize * (margin / 100);

        const cell =
            (canvasSize - padding * 2) / count;

        let previewBg = bgColor;

        if (isLightColor(color1)) {
            previewBg = "#111111";
        }

        // Background
        if (!transparentBg) {
            ctx.fillStyle = previewBg;

            ctx.fillRect(
                0,
                0,
                qrWidth,
                qrHeight
            );
        }

        else {
            ctx.clearRect(
                0,
                0,
                qrWidth,
                qrHeight
            );
        }

        ctx.fillStyle = color1;
        ctx.strokeStyle = color1;

        // Draw data patterns
        matrix.forEach((row, r) => {

            row.forEach((col, c) => {

                if (!col) return;

                const isFinder =
                    (r < 7 && c < 7) ||
                    (r < 7 && c > count - 8) ||
                    (r > count - 8 && c < 7);

                if (isFinder) return;

                const x =
                    padding + c * cell;

                const y =
                    padding + r * cell;

                drawPattern(
                    ctx,
                    patternStyle,
                    x,
                    y,
                    cell
                );
            });
        });

        // Finder corners
        const finderSize = cell * 7;

        ctx.lineWidth = cell * 0.8;



        drawCorner(
            ctx,
            cornerStyle,
            padding,
            padding,
            finderSize
        );

        drawCorner(
            ctx,
            cornerStyle,
            canvasSize - finderSize - padding,
            padding,
            finderSize
        );

        drawCorner(
            ctx,
            cornerStyle,
            padding,
            canvasSize - finderSize - padding,
            finderSize
        );

        if (showCorners) {
            drawScannerBorder(
                ctx,
                canvasSize,
                padding,
                color1
            );
        }

        // Logo
        if (logo) {

            const img = new Image();

            img.src = logo;

            img.onload = () => {

                const logoSize = canvasSize * 0.11;

                const logoPadding =
                    logoSize * 0.25;

                const x =
                    canvasSize / 2 - logoSize / 2;

                const y =
                    canvasSize / 2 - logoSize / 2;

                ctx.beginPath();

                ctx.arc(
                    canvasSize / 2,
                    canvasSize / 2,
                    logoSize / 2 + logoPadding,
                    0,
                    Math.PI * 2
                );

                ctx.fillStyle = "#ffffff";

                ctx.fill();

                ctx.save();

                ctx.beginPath();

                ctx.arc(
                    canvasSize / 2,
                    canvasSize / 2,
                    logoSize / 2,
                    0,
                    Math.PI * 2
                );

                ctx.clip();

                ctx.drawImage(
                    img,
                    x,
                    y,
                    logoSize,
                    logoSize
                );

                ctx.restore();
            };
        }
    }

    // =========================================================
    // EXPORTS
    // =========================================================

    function downloadPNG() {

        const canvas = canvasRef.current;

        const link =
            document.createElement("a");

        link.download = "qr-code.png";

        link.href =
            canvas.toDataURL("image/png");

        link.click();
    }

    function downloadJPG() {

        const canvas = canvasRef.current;

        const tempCanvas =
            document.createElement("canvas");

        tempCanvas.width = canvas.width;
        tempCanvas.height = canvas.height;

        const tempCtx =
            tempCanvas.getContext("2d");

        // JPG doesn't support transparency
        tempCtx.fillStyle = "#ffffff";

        tempCtx.fillRect(
            0,
            0,
            tempCanvas.width,
            tempCanvas.height
        );

        tempCtx.drawImage(canvas, 0, 0);

        const link =
            document.createElement("a");

        link.download = "qr-code.jpg";

        link.href =
            tempCanvas.toDataURL(
                "image/jpeg",
                1
            );

        link.click();
    }

    function downloadSVG() {

        if (!window.QRCode) return;

        const qr = new window.QRCode(
            document.createElement("div"),
            {
                text,
                width: qrWidth,
                height: qrHeight,
                correctLevel:
                    window.QRCode.CorrectLevel.H,
            }
        );

        const matrix = qr._oQRCode.modules;

        const count = matrix.length;

        const canvasSize =
            Math.min(qrWidth, qrHeight);

        const padding = canvasSize * (margin / 100);;

        const cell =
            (canvasSize - padding * 2) / count;

        let svg = `
<svg xmlns="http://www.w3.org/2000/svg"
width="${qrWidth}"
height="${qrHeight}"
viewBox="0 0 ${qrWidth} ${qrHeight}">
`;

        if (!transparentBg) {
            svg += `
<rect
width="100%"
height="100%"
fill="${bgColor}"
/>
`;
        }

        matrix.forEach((row, r) => {

            row.forEach((col, c) => {

                if (!col) return;

                const isFinder =
                    (r < 7 && c < 7) ||
                    (r < 7 && c > count - 8) ||
                    (r > count - 8 && c < 7);

                if (isFinder) return;

                const x =
                    padding + c * cell;

                const y =
                    padding + r * cell;

                svg += `
<rect
x="${x}"
y="${y}"
width="${cell}"
height="${cell}"
fill="${color1}"
rx="${patternStyle === "round"
                        ? cell * 0.3
                        : 0
                    }"
/>
`;
            });
        });

        svg += `</svg>`;

        const blob = new Blob(
            [svg],
            {
                type: "image/svg+xml",
            }
        );

        const url =
            URL.createObjectURL(blob);

        const link =
            document.createElement("a");

        link.href = url;

        link.download = "qr-code.svg";

        link.click();

        URL.revokeObjectURL(url);
    }

    // =========================================================
    // UI
    // =========================================================

    const isQrLight =
        isLightColor(color1);

    return (
        <div className="sm:px-6">

            {/* Header */}
            <div className="text-start">

                <div className="inline-flex items-center gap-3 backdrop-blur-sm rounded-2xl px-0 py-1 border border-white/20 mb-1">

                    <h1 className="premium-page-title">
                        Create dynamic QR codes
                    </h1>

                </div>

                <p className="text-sm text-black-500">
                    Design, customize, and generate branded QR codes in seconds.
                </p>
            </div>

            <div className="flex flex-col lg:flex-row py-5 gap-10">

                {/* QR Preview */}
                <div className="flex-1 flex items-center justify-center">

                    <div
                        className={`shadow-lg ${isQrLight
                            ? "bg-gray-900"
                            : "bg-white"
                            }`}
                    >
                        <canvas
                            ref={canvasRef}
                            className="w-[400px] h-[400px]"
                        />
                    </div>
                </div>

                {/* Controls */}
                <div className="w-full lg:w-80 bg-white rounded-xl shadow-lg p-6 space-y-5 h-full">

                    <h2 className="text-xl font-semibold">
                        Dynamic QR code
                    </h2>

                    {/* URL */}
                    <div>
                        <label className="text-sm">
                            URL / Text
                        </label>

                        <input
                            className="mt-1 w-full border rounded p-2"
                            value={text}
                            maxLength={100}
                            onChange={(e) =>
                                setText(e.target.value)
                            }
                        />
                    </div>

                    {/* Pattern */}
                    <div className="form-group">

                        <label className="text-sm">
                            Pattern
                        </label>

                        <div className="relative">

                            <select
                                className="appearance-none"
                                value={patternStyle}
                                onChange={(e) =>
                                    setPatternStyle(
                                        e.target.value
                                    )
                                }
                            >
                                <option value="round">
                                    Rounded
                                </option>

                                <option value="square">
                                    Square
                                </option>

                                <option value="diamond">
                                    Diamond
                                </option>

                                <option value="dot">
                                    Dot
                                </option>

                                <option value="star">
                                    Star
                                </option>
                            </select>

                            <ChevronDown
                                size={18}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none"
                            />
                        </div>
                    </div>

                    {/* Corner style */}
                    <div className="form-group">

                        <label className="text-sm">
                            Corner Style
                        </label>

                        <div className="relative">

                            <select
                                className="appearance-none"
                                value={cornerStyle}
                                onChange={(e) =>
                                    setCornerStyle(
                                        e.target.value
                                    )
                                }
                            >
                                <option value="square">
                                    Square
                                </option>

                                <option value="round">
                                    Rounded
                                </option>

                                <option value="circle">
                                    Circle
                                </option>
                            </select>

                            <ChevronDown
                                size={18}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none"
                            />
                        </div>
                    </div>

                    {/* Width + Height */}
                    <div className="grid grid-cols-2 gap-3">

                        <div>
                            <label className="text-sm">
                                Width
                            </label>

                            <input
                                type="number"
                                min="200"
                                max="4000"
                                value={qrWidth}
                                onChange={(e) =>
                                    setQrWidth(
                                        Number(e.target.value)
                                    )
                                }
                                className="mt-1 w-full border rounded p-2"
                            />
                        </div>

                        <div>
                            <label className="text-sm">
                                Height
                            </label>

                            <input
                                type="number"
                                min="200"
                                max="4000"
                                value={qrHeight}
                                onChange={(e) =>
                                    setQrHeight(
                                        Number(e.target.value)
                                    )
                                }
                                className="mt-1 w-full border rounded p-2"
                            />
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center justify-between">
                            <label className="text-sm">
                                Margin
                            </label>

                            <span className="text-xs text-gray-500">
                                {margin}%
                            </span>
                        </div>

                        <input
                            type="range"
                            min="0"
                            max="30"
                            value={margin}
                            onChange={(e) =>
                                setMargin(Number(e.target.value))
                            }
                            className="w-full"
                        />
                    </div>

                    {/* QR color */}
                    <div>
                        <label className="text-sm">
                            Code Color
                        </label>

                        <input
                            type="color"
                            value={color1}
                            onChange={(e) =>
                                setColor1(e.target.value)
                            }
                            className="w-full h-10"
                        />
                    </div>

                    {/* Background */}
                    <div>
                        <label className="text-sm">
                            Background Color
                        </label>

                        <input
                            type="color"
                            value={bgColor}
                            onChange={(e) =>
                                setBgColor(e.target.value)
                            }
                            className="w-full h-10"
                        />
                    </div>

                    {/* Transparent */}
                    <div className="flex items-center gap-2">

                        <input
                            type="checkbox"
                            id="transparent-bg"
                            checked={transparentBg}
                            onChange={(e) =>
                                setTransparentBg(
                                    e.target.checked
                                )
                            }
                        />

                        <label
                            className="text-sm"
                            htmlFor="transparent-bg"
                        >
                            Transparent Background
                        </label>
                    </div>

                    {/* Toggle corners */}
                    <div className="flex items-center gap-2">

                        <input
                            type="checkbox"
                            id="show-corners"
                            checked={showCorners}
                            onChange={(e) =>
                                setShowCorners(
                                    e.target.checked
                                )
                            }
                        />

                        <label
                            htmlFor="show-corners"
                            className="text-sm"
                        >
                            Show Scanner Corners
                        </label>
                    </div>

                    {/* Export Buttons */}
                    <div className="grid grid-cols-2 gap-2">

                        <button
                            onClick={downloadPNG}
                            className="bg-black text-white py-2 rounded"
                        >
                            PNG
                        </button>

                        <button
                            onClick={downloadJPG}
                            className="bg-black text-white py-2 rounded"
                        >
                            JPG
                        </button>

                        {/* <button
                            onClick={downloadSVG}
                            className="bg-black text-white py-2 rounded"
                        >
                            SVG
                        </button> */}
                    </div>
                </div>
            </div>
        </div>
    );
}